import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import {
  getDashboardSummary,
  getExternalEventsSummary,
} from "../../services/dashboardService";
import useCounterAnimation from "../../hooks/useCounterAnimation";
import TableHeader from "../Common/TableComponent/TableHeader";
import CardSkeleton from "../Common/Loaders/CardSkeleton";
import { handleErrors } from "../../utils/errorHandler";

const SUBSCRIBERS_COLUMNS = ["SL No", "Email Address", "Subscribed On"];

const getSubscriberEmail = (subscriber) =>
  subscriber.email ??
  subscriber.Email ??
  subscriber.emailId ??
  subscriber.EmailId ??
  "-";

const getSubscriberAddedOn = (subscriber) =>
  subscriber.addedOn ??
  subscriber.AddedOn ??
  subscriber.createdOn ??
  subscriber.CreatedOn ??
  "";

const formatAddedOn = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
};

export const DashboardSummary = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    publishedArticles: 0,
    unpublishedArticles: 0,
    emailSubscribers: 0,
    advertisementBanners: 0,
    recentSubscribers: [],
  });
  const [eventsSummary, setEventsSummary] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    liveEvents: 0,
    expiredEvents: 0,
  });

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const [summaryResponse, eventsResponse] = await Promise.all([
          getDashboardSummary(),
          getExternalEventsSummary(),
        ]);
        const result = summaryResponse?.result || {};
        setSummary({
          publishedArticles: result.publishedArticles ?? 0,
          unpublishedArticles: result.unpublishedArticles ?? 0,
          emailSubscribers: result.emailSubscribers ?? 0,
          advertisementBanners: result.advertisementBanners ?? 0,
          recentSubscribers: Array.isArray(result.recentSubscribers)
            ? result.recentSubscribers
            : [],
        });

        const eventsResult = eventsResponse?.result || {};
        setEventsSummary({
          totalEvents:
            eventsResult.totalEvents ?? eventsResult.TotalEvents ?? 0,
          upcomingEvents:
            eventsResult.upcomingEvents ?? eventsResult.UpcomingEvents ?? 0,
          liveEvents: eventsResult.liveEvents ?? eventsResult.LiveEvents ?? 0,
          expiredEvents:
            eventsResult.expiredEvents ?? eventsResult.ExpiredEvents ?? 0,
        });
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  const publishedArticles = useCounterAnimation(
    summary.publishedArticles,
    1600,
  );
  const unpublishedArticles = useCounterAnimation(
    summary.unpublishedArticles,
    1600,
  );
  const emailSubscribers = useCounterAnimation(summary.emailSubscribers, 1600);
  const advertisementBanners = useCounterAnimation(
    summary.advertisementBanners,
    1600,
  );

  const totalArticles = summary.publishedArticles + summary.unpublishedArticles;
  const publishRate =
    totalArticles > 0
      ? Math.round((summary.publishedArticles / totalArticles) * 100)
      : 0;

  const cards = [
    {
      title: "Published Articles",
      subtitle: "Live on site",
      value: publishedArticles,
      icon: "ri-article-line",
      tone: "success",
      badge: `${publishRate}% published`,
      badgeTone: "success",
      route: "/articles",
      linkLabel: "View Articles",
    },
    {
      title: "Draft Articles",
      subtitle: "In progress",
      value: unpublishedArticles,
      icon: "ri-draft-line",
      tone: "warning",
      badge: "Needs review",
      badgeTone: "warning",
      route: "/articles",
      linkLabel: "Open Drafts",
    },
    {
      title: "Email Subscribers",
      subtitle: "Newsletter list",
      value: emailSubscribers,
      icon: "ri-mail-line",
      tone: "info",
      badge: "Active list",
      badgeTone: "info",
      route: "/manage-subscribers",
      linkLabel: "View Subscribers",
    },
    {
      title: "Advertisement",
      subtitle: "Active banners",
      value: advertisementBanners,
      icon: "ri-advertisement-line",
      tone: "dark",
      badge: "Campaigns",
      badgeTone: "dark",
      route: "/advertisements",
      linkLabel: "Manage Ads",
    },
  ];

  const quickActions = [
    {
      label: "Add Article",
      icon: "ri-add-circle-line",
      route: "/articles/add",
    },
    {
      label: "Add Advertisement",
      icon: "ri-image-add-line",
      route: "/advertisements",
    },
    {
      label: "Manage Sections",
      icon: "ri-layout-grid-line",
      route: "/manage-sections",
    },
    {
      label: "Manage Subscribers",
      icon: "ri-user-follow-line",
      route: "/manage-subscribers",
    },
  ];

  const overviewLanes = [
    {
      title: "Live Events",
      meta: "Happening now",
      value: eventsSummary.liveEvents,
      color: "#16a34a",
      icon: "ri-broadcast-line",
    },
    {
      title: "Upcoming Events",
      meta: "Scheduled",
      value: eventsSummary.upcomingEvents,
      color: "#58BDE2",
      icon: "ri-calendar-event-line",
    },
    {
      title: "Expired Events",
      meta: "Past events",
      value: eventsSummary.expiredEvents,
      color: "#6b7280",
      icon: "ri-history-line",
    },
  ].map((item) => {
    const pct =
      eventsSummary.totalEvents > 0
        ? Math.round((item.value / eventsSummary.totalEvents) * 100)
        : 0;
    return {
      ...item,
      pct,
      barWidth: eventsSummary.totalEvents > 0 ? Math.max(6, pct) : 0,
    };
  });

  const growthChart = useMemo(() => {
    const publishedSeries = [
      18,
      24,
      22,
      30,
      28,
      36,
      40,
      38,
      46,
      52,
      58,
      summary.publishedArticles || 60,
    ];
    const subscriberSeries = [
      3,
      4,
      5,
      5,
      6,
      7,
      8,
      9,
      10,
      11,
      12,
      summary.emailSubscribers || 13,
    ];

    return {
      series: [
        { name: "Published", data: publishedSeries },
        { name: "Subscribers", data: subscriberSeries },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "Roboto, sans-serif",
          animations: { enabled: true, speed: 650 },
          parentHeightOffset: 0,
        },
        colors: ["#58BDE2", "#1F1D1F"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "48%",
            borderRadius: 6,
            borderRadiusApplication: "end",
            dataLabels: { position: "top" },
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif",
          fontWeight: 500,
          markers: { width: 8, height: 8, radius: 8 },
          itemMargin: { horizontal: 10 },
        },
        xaxis: {
          categories: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: "#9ca3af",
              fontSize: "11px",
              fontFamily: "Roboto, sans-serif",
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#9ca3af",
              fontSize: "11px",
              fontFamily: "Roboto, sans-serif",
            },
          },
        },
        grid: {
          borderColor: "#eef1f5",
          strokeDashArray: 4,
          padding: { top: -10, right: 0, bottom: 0, left: 8 },
          xaxis: { lines: { show: false } },
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: "light",
          style: { fontFamily: "Roboto, sans-serif" },
          y: {
            formatter: (val) => `${val}`,
          },
        },
        fill: { opacity: 1 },
        responsive: [
          {
            breakpoint: 992,
            options: {
              legend: {
                position: "bottom",
                horizontalAlign: "center",
              },
              plotOptions: {
                bar: { columnWidth: "52%" },
              },
            },
          },
          {
            breakpoint: 576,
            options: {
              legend: {
                position: "bottom",
                horizontalAlign: "center",
                fontSize: "11px",
              },
              plotOptions: {
                bar: { columnWidth: "60%", borderRadius: 4 },
              },
              xaxis: {
                labels: { style: { fontSize: "10px" } },
              },
            },
          },
        ],
      },
    };
  }, [summary.publishedArticles, summary.emailSubscribers]);

  if (loading) {
    return (
      <>
        <div className="row g-3 mb-4">
          <CardSkeleton count={4} withGrid />
        </div>
        <div className="row g-3">
          <div className="col-xl-7">
            <div className="xc-skel-card" style={{ minHeight: 280 }} />
          </div>
          <div className="col-xl-5">
            <div className="xc-skel-card" style={{ minHeight: 280 }} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Stat cards */}
      <div className="row g-3 mb-4">
        {cards.map((card, index) => (
          <div
            className="col-xl-3 col-md-6 dash-fade"
            key={card.title}
            style={{ animationDelay: `${index * 0.08}s` }}
          >
            <div
              className={`card dash-stat-card dash-stat-card--${card.tone} mb-0`}
            >
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <p className="dash-stat-card__label mb-0">{card.title}</p>
                    <span className="dash-stat-card__subtitle">
                      {card.subtitle}
                    </span>
                  </div>
                  <span className={`dash-stat-card__icon tone-${card.tone}`}>
                    <i className={card.icon}></i>
                  </span>
                </div>

                <h3 className="dash-stat-card__value mb-0">{card.value}</h3>

                <div className="dash-stat-card__footer">
                  <span className={`dash-badge dash-badge--${card.badgeTone}`}>
                    {card.badge}
                  </span>
                  <Link to={card.route} className="dash-stat-card__link">
                    {card.linkLabel}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Growth Trend (left) + Content Overview (right) */}
      <div className="row g-3 mb-4">
        <div className="col-xl-8 dash-fade" style={{ animationDelay: "0.2s" }}>
          <div className="card dash-panel dash-cluster mb-0 h-100">
            <div className="card-header border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">Growth Trend</h5>
                  <p className="dash-panel__hint mb-0">
                    Published articles vs subscribers
                  </p>
                </div>
                <span className="dash-pill dash-pill--peak">This year</span>
              </div>
            </div>
            <div className="card-body pt-1 pb-2">
              <Chart
                options={growthChart.options}
                series={growthChart.series}
                type="bar"
                height={280}
              />
            </div>
          </div>
        </div>

        <div className="col-xl-4 dash-fade" style={{ animationDelay: "0.28s" }}>
          <div className="card dash-panel dash-lanes mb-0 h-100">
            <div className="card-header border-0 pb-1">
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div>
                  <h5 className="mb-0">Event Overview</h5>
                  <p className="dash-panel__hint mb-0">
                    Share of {eventsSummary.totalEvents.toLocaleString()} total
                    events
                  </p>
                </div>
                <Link to="/events" className="dash-stat-card__link">
                  View all
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="dash-lanes__scroll">
                <div className="dash-lanes__list">
                  {overviewLanes.map((lane) => (
                    <div className="dash-lane" key={lane.title}>
                      <div className="dash-lane__top">
                        <div className="dash-lane__title">
                          <span
                            className="dash-lane__icon"
                            style={{
                              background: `${lane.color}18`,
                              color: lane.color,
                            }}
                          >
                            <i className={lane.icon}></i>
                          </span>
                          <div>
                            <strong>{lane.title}</strong>
                            <p className="dash-lane__meta mb-0">{lane.meta}</p>
                          </div>
                        </div>
                        <span className="dash-lane__value">
                          {lane.value.toLocaleString()}
                        </span>
                      </div>
                      <div className="dash-lane__progress">
                        <div className="dash-lane__track">
                          <span
                            style={{
                              width: `${lane.barWidth}%`,
                              background: lane.color,
                            }}
                          />
                        </div>
                        <span className="dash-lane__pct">{lane.pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table + actions */}
      <div className="row g-3">
        <div className="col-xl-8 dash-fade" style={{ animationDelay: "0.35s" }}>
          <div className="card dash-panel dash-subscribers mb-0">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Recent Subscribers</h5>
              <Link to="/manage-subscribers" className="dash-stat-card__link">
                View all
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle dash-table mb-0">
                  <TableHeader columns={SUBSCRIBERS_COLUMNS} />
                  <tbody>
                    {summary.recentSubscribers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={SUBSCRIBERS_COLUMNS.length}
                          className="text-center text-muted py-4"
                        >
                          No recent subscribers available.
                        </td>
                      </tr>
                    ) : (
                      summary.recentSubscribers.map((subscriber, index) => (
                        <tr key={subscriber.id ?? subscriber.Id ?? index}>
                          <td>
                            <span className="dash-index">{index + 1}</span>
                          </td>
                          <td className="fw-medium">
                            {getSubscriberEmail(subscriber)}
                          </td>
                          <td className="text-muted">
                            {formatAddedOn(getSubscriberAddedOn(subscriber))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 dash-fade" style={{ animationDelay: "0.42s" }}>
          <div className="card dash-panel mb-3">
            <div className="card-header">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column gap-2">
                {quickActions.map((action) => (
                  <Link
                    key={action.label}
                    to={action.route}
                    className="dash-action"
                  >
                    <span className="dash-action__icon">
                      <i className={action.icon}></i>
                    </span>
                    <span className="dash-action__label">{action.label}</span>
                    <i className="dash-action__arrow ri-arrow-right-line"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="card dash-panel mb-0">
            <div className="card-header">
              <h5 className="mb-0">Content Health</h5>
            </div>
            <div className="card-body">
              <div className="dash-health">
                <div className="dash-health__row">
                  <span>Publish rate</span>
                  <strong>{publishRate}%</strong>
                </div>
                <div className="dash-health__bar">
                  <span style={{ width: `${publishRate}%` }} />
                </div>
                <div className="dash-health__meta">
                  <span>
                    <i className="ri-checkbox-circle-line text-success"></i>
                    {summary.publishedArticles} live
                  </span>
                  <span>
                    <i className="ri-time-line text-warning"></i>
                    {summary.unpublishedArticles} drafts
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSummary;
